import * as React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { FieldConfig } from 'react-reactive-form';
import { connect, useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import ReactTable, { Column, FinalState, RowInfo } from 'react-table';
import {
    closeAllModals,
    FacilitiesSearchParams,
    getFacilitiesByCountry,
    searchFacilityQueueForAsyncSelect,
    toggleEditFacilityModal
} from '../../actions/commonActions';
import {
    mergeFacilitiesAPI,
    setSelectedFacilityID,
    setTableFilter
} from '../../actions/manageFacilityActions';
import { getProductInfo } from '../../actions/manageInventoryActions';
import { constants } from '../../constants/constants';
import {
    Ifacility,
    Ioption,
    ItableFiltersReducer,
    Itile,
    Iuser
} from '../../models';
import { IinitialState } from '../../reducers';
import { selectIsLoading } from '../../reducers/commonReducers';
import { initialFacility, emptyTile } from '../../reducers/initialState';
import { selectManageFacilityTableFilters } from '../../reducers/manageCustomerAndFacilityReducer';
import { Banner } from '../common/Banner';
import EditFacilityModal from '../common/EditFacilityModal';
import FacilityContactModal from '../common/FacilityContactModal';
import FacilityContractModal from '../common/FacilityContractModal';
import { FormUtil } from '../common/FormUtil';
import { toastr } from 'react-redux-toastr';
import { debounce } from 'lodash';
import SearchFacilityModal from '../manageFacility/searchFacilityModal';
import { beginAjaxCall, endAjaxCall } from '../../actions/ajaxStatusActions';
import * as types from '../../actions/actionTypes';
import SearchTableForm from '../common/SearchTableForm';
import { getCountries } from '../../reducers/countriesReducer';
import { getProductInfo as getProductInfoSelector } from '../../reducers/productInfoReducer';
import { getUsersCountries } from '../../reducers/userReducer';
import {
    formatFacilityStreet,
    getFacilityDisplayName
} from '../../helpers/stringHelpers';

interface RowInfoFacility extends RowInfo {
    original: Ifacility;
}

type Iprops = RouteComponentProps<any>;

interface IdispatchProps {
    totalPages: number;
    setTableFilter: typeof setTableFilter;
    tableFilters: ItableFiltersReducer;
    loading: boolean;
    setSelectedFacilityID: typeof setSelectedFacilityID;
    toggleEditFacilityModal: typeof toggleEditFacilityModal;
    getProductInfo: typeof getProductInfo;
    c: number;
    user: Iuser;
    closeAllModals: typeof closeAllModals;
    searchFacilityQueueForAsyncSelect: typeof searchFacilityQueueForAsyncSelect;
    beginAjaxCall: typeof beginAjaxCall;
    endAjaxCall: typeof endAjaxCall;
}

interface Istate {
    selectedRow: any;
    columns: any[];
    searchFieldConfig: FieldConfig;
    tableData: Ifacility[];
    currentTile: Itile;
    filter: FacilitiesSearchParams;
    selectedFacility: Ifacility;
    showSearchFacilityModal: boolean;
}

const ManangeFacilityQueue: React.FC<Iprops &
    IdispatchProps &
    WithTranslation &
    Istate> = props => {
    const { t, i18n } = props;
    const dispatch = useDispatch();
    const countries = useSelector(getCountries);
    const usersCountries = useSelector(getUsersCountries);
    const productInfo = useSelector(getProductInfoSelector);

    const buildSearchControls = (): FieldConfig => {
        const countriesOptions: Ioption[] = countries
            .filter(c => usersCountries?.includes(c.id))
            .map(c => ({ label: c.name, value: c.id }));

        // Order is Facility, Country, Standard at 1/4 each, matching the
        // Manage Facility page, with the SAP checkbox keeping the last quarter
        // so all four filters stay on one row (MMG-4352).
        const mainSearchControls = {
            facilityName: {
                render: FormUtil.TextInputWithoutValidation,
                meta: {
                    label: t('manageFacility:facility'),
                    colWidth: 3,
                    type: 'text',
                    placeholder: t('manageFacility:facilitySearchPlaceholder'),
                    defaultValue: props.tableFilters.facilityName,
                    isClearable: true
                },
                formState: {
                    value: props.tableFilters.facilityName,
                    disabled: false
                }
            },
            country: {
                render: FormUtil.Select,
                meta: {
                    options: countriesOptions,
                    label: t('manageFacility:Country'),
                    colWidth: 3,
                    colWidthMedium: 3,
                    colWidthLarge: 3,
                    placeholder: t('common:searchPlaceholder'),
                    name: 'country',
                    defaultValue: '',
                    isClearable: true
                }
            },
            standard: {
                render: FormUtil.MultiSelectFilter,
                meta: {
                    options: productInfo.standardOptions,
                    label: t('common:standard'),
                    colWidth: 3,
                    colWidthMedium: 3,
                    colWidthLarge: 3,
                    name: 'standard',
                    defaultValue: '',
                    t
                }
            },
            hasSAPNumber: {
                render: FormUtil.Checkbox,
                meta: {
                    label: t('hasSAPNumber'),
                    colWidth: 3
                },
                formState: {
                    value: false,
                    disabled: false
                }
            }
        };
        return FormUtil.translateForm(
            {
                controls: { ...mainSearchControls }
            },
            props.t
        );
    };

    const [state, setState] = React.useState<Istate>({
        selectedRow: {},
        columns: [],
        tableData: [],
        searchFieldConfig: { controls: [] },
        currentTile: emptyTile,
        filter: {
            name: '',
            address: '',
            postalCode: ''
        },
        selectedFacility: initialFacility,
        showSearchFacilityModal: false
    });

    // countryID -> SAP code, for the DE/AT name concatenation (MMG-4192).
    const countryCodeById = React.useMemo(() => {
        const map: { [id: string]: string | undefined } = {};
        countries.forEach(c => (map[c.id] = c.sapCountryCode));
        return map;
    }, [countries]);

    const columns = React.useMemo(
        () => [
            {
                Header: t('manageInventory:name'),
                id: 'name',
                accessor: (f: Ifacility) =>
                    getFacilityDisplayName(
                        f.name,
                        f.name2,
                        countryCodeById[f.countryID]
                    ),
                minWidth: 200
            },
            {
                Header: t('manageFacility:sapFacilityNumber'),
                id: 'sapFacilityNumber',
                accessor: 'sapFacilityNumber',
                minWidth: 150
            },
            {
                Header: t('manageFacility:address'),
                id: 'address',
                accessor: ({
                    address,
                    address2,
                    city,
                    state: facilityState,
                    postalCode,
                    countryID
                }: Ifacility) => {
                    const street = formatFacilityStreet(
                        address,
                        address2,
                        countryID
                    );
                    return (
                        `${street},` +
                        ` ${city ? city : ''},` +
                        ` ${facilityState ? facilityState : ''},` +
                        ` ${postalCode ? postalCode : ''}`
                    );
                },
                minWidth: 300
            }
        ],
        [t, countryCodeById]
    );

    React.useEffect(() => {
        setState(prevState => ({
            ...prevState,
            columns
        }));
    }, [columns]);

    const [hasSAPNumChecked, setHasSAPNumChecked] = React.useState<boolean>(
        false
    );
    const [
        allFacillitesWithSearchTerm,
        setAllFacilitiesWithSearchTerm
    ] = React.useState<Ifacility[]>([]);

    const [selectedCountry, setSelectedCountry] = React.useState<
        string | undefined
    >(undefined);
    const [facilityNameSearch, setFacilityNameSearch] = React.useState<
        string | undefined
    >(undefined);
    const [selectedStandardIDs, setSelectedStandardIDs] = React.useState<
        string[]
    >([]);

    // The Standard filter needs productInfo, which this page never loaded.
    React.useEffect(() => {
        if (!productInfo.standardOptions.length) {
            dispatch(getProductInfo());
        }
        // eslint-disable-next-line
    }, []);

    // standardOptions arrive after the first render, so rebuild the controls
    // once they land or the Standard filter would have no options (MMG-4352).
    // Guarded so this is the single extra rebuild, not one on every mount pass.
    React.useEffect(() => {
        if (!productInfo.standardOptions.length) {
            return;
        }

        setState(prevState => ({
            ...prevState,
            searchFieldConfig: buildSearchControls()
        }));
        // eslint-disable-next-line
    }, [productInfo.standardOptions]);

    React.useEffect(() => {
        if (state.searchFieldConfig?.controls.length === 0) {
            setState(prevState => ({
                ...prevState,
                searchFieldConfig: buildSearchControls()
            }));
        }
        // eslint-disable-next-line
    }, [state.searchFieldConfig, i18n.language]);

    React.useEffect(() => {
        const tableData = filterSAPNumber(
            allFacillitesWithSearchTerm,
            hasSAPNumChecked
        );
        setState(prevState => ({
            ...prevState,
            tableData
        }));
    }, [hasSAPNumChecked, allFacillitesWithSearchTerm]);

    const colorButton =
        constants.colors[
            `${state.currentTile.color}Button` as keyof typeof constants.colors
        ];

    const getTdProps = (
        finalState: FinalState,
        rowInfo: RowInfoFacility | undefined,
        column: Column | undefined,
        instance: any
    ) => {
        if (rowInfo) {
            return {
                onClick: () => {
                    props.setSelectedFacilityID(rowInfo.original.id);
                    setState({ ...state, selectedFacility: rowInfo.original });
                    props.toggleEditFacilityModal();
                }
            };
        } else {
            return {};
        }
    };

    const onPageChange = (page: number) => {
        props.setTableFilter({ page });
        setState({ ...state, selectedRow: {} });
    };

    const onPageSizeChange = (rows: number) => {
        props.setTableFilter({ rows, page: 0 });
        setState({ ...state, selectedRow: {} });
    };

    const hideMergeFacilitiesModal = () => {
        setState({ ...state, showSearchFacilityModal: false });
    };

    const mergeFacilities = async (primary: string, secondary: string[]) => {
        const response = await mergeFacilitiesAPI(primary, secondary);
        if (response) {
            // Remove the now merged facilities from redux if they're there
            dispatch({
                type: types.MERGE_FACILITIES_SUCCESS,
                primaryId: primary,
                secondaryIds: secondary
            });

            // Remove the now merged facilities from state
            const updatedTableData = allFacillitesWithSearchTerm.filter(
                tempFacility => {
                    if (secondary.includes(tempFacility.id)) {
                        return false;
                    }

                    return true;
                }
            );

            setAllFacilitiesWithSearchTerm(updatedTableData);
            setState(prevState => ({
                ...prevState,
                showSearchFacilityModal: false
            }));

            props.toggleEditFacilityModal();

            toastr.success(
                t('Success'),
                t('Facilities successfully merged'),
                constants.toastrSuccess
            );
        } else {
            toastr.warning(
                t('Error'),
                t('Failed to merge Facilities'),
                constants.toastrError
            );
        }
    };

    const searchForFacility = (facilityID: string) => {
        setState({ ...state, showSearchFacilityModal: true });
    };

    const saveFacilities = (facilities: Ifacility[]) => {
        const updatedTableData = facilities.filter(tempFacility => {
            if (tempFacility.isApproved) {
                return false;
            }

            return true;
        });
        setAllFacilitiesWithSearchTerm(updatedTableData);
        props.endAjaxCall();
    };

    const filterSAPNumber = (
        allFacilities: Ifacility[],
        hasSAPNumber: boolean
    ) =>
        hasSAPNumber
            ? allFacilities.filter(
                  f =>
                      f.sapFacilityNumber !== '' &&
                      f.sapFacilityNumber !== undefined &&
                      f.sapFacilityNumber !== null
              )
            : allFacilities;

    const debouncedSearch = React.useRef(
        debounce(async (searchTerms, countryId, standardIDs) => {
            props.beginAjaxCall();

            props.searchFacilityQueueForAsyncSelect(
                searchTerms,
                countryId,
                () => {
                    return;
                },
                saveFacilities,
                standardIDs
            );
        }, 200)
    ).current;

    const handleNameChange = (searchTerms: string) => {
        if (searchTerms.length >= 3) {
            setFacilityNameSearch(searchTerms);
            debouncedSearch(searchTerms, selectedCountry, selectedStandardIDs);
            return;
        }

        if (searchTerms.length === 0) {
            setFacilityNameSearch(undefined);
            debouncedSearch(undefined, selectedCountry, selectedStandardIDs);
            return;
        }
    };

    const handleSAPNumChange = (value: boolean) => {
        setHasSAPNumChecked(value);
    };

    const handleCountryChange = (countryId: string | undefined) => {
        setSelectedCountry(countryId);
        debouncedSearch(facilityNameSearch, countryId, selectedStandardIDs);
    };

    const handleStandardChange = (standardIDs: string[]) => {
        setSelectedStandardIDs(standardIDs);
        debouncedSearch(facilityNameSearch, selectedCountry, standardIDs);
    };

    // Re-run the search that is currently on screen. Passing undefined here
    // refetched the whole queue while the search form still showed the user's
    // country/name filters. The hasSAPNumber filter is applied client-side, so
    // it re-applies on its own once the new results land.
    const refreshData = () => {
        debouncedSearch(
            facilityNameSearch,
            selectedCountry,
            selectedStandardIDs
        );
    };

    const onValueChanges = (value: any, key: string) => {
        switch (key) {
            case 'facilityName':
                handleNameChange(value);
                break;
            case 'hasSAPNumber':
                handleSAPNumChange(value);
                break;
            case 'country':
                if (value === null) {
                    handleCountryChange(undefined); // if null, that means the user cleared the value
                } else {
                    handleCountryChange(value.value); // value will be an Ioption
                }
                break;
            case 'standard':
                // multi-select: an array of Ioption, or null once cleared
                handleStandardChange(
                    value ? value.map((option: Ioption) => option.value) : []
                );
                break;
            default:
                break;
        }
    };

    React.useEffect(() => {
        props.beginAjaxCall();
        props.searchFacilityQueueForAsyncSelect(
            undefined,
            undefined,
            () => {
                return;
            },
            saveFacilities,
            []
        );
        // eslint-disable-next-line
    }, []);

    return (
        <>
            <Banner
                title={t('manageFacility:facilityQueueTitle')}
                img={state.currentTile.srcBanner}
                color={state.currentTile.color}
                subtitle={props.selectedFacility?.name}
            />
            <SearchTableForm
                fieldConfig={state.searchFieldConfig}
                loading={props.loading}
                showSearchButton={false}
                colorButton={colorButton}
                t={props.t}
                onValueChanges={onValueChanges}
                subscribeValueChanges={true}
                showAdditionalFiltersButton={false}
                showAdditionalFilters={false}
                hasActiveAdditionalFilters={false}
                handleSubmit={() => {}}
            ></SearchTableForm>
            <ReactTable
                data={state.tableData}
                columns={state.columns}
                getTdProps={getTdProps}
                defaultPageSize={
                    props.tableFilters.rows || constants.tablePageSizeDefault
                }
                onPageSizeChange={onPageSizeChange}
                showPageSizeOptions={true}
                pageSizeOptions={constants.tablePageSizeOptions}
                className={`beacon-table -highlight ${state.currentTile.color}`}
                previousText={t('common:previous')}
                nextText={t('common:next')}
                onPageChange={onPageChange}
                sortable={false}
                multiSort={false}
                noDataText={t('common:noDataSearch')}
                resizable={false}
                loading={state.tableData.length === 0 && props.loading}
            />

            <EditFacilityModal
                colorButton={colorButton}
                selectedFacility={state.selectedFacility}
                disabled={false}
                approveMode={true}
                searchForFacility={searchForFacility}
                showSearchFacilityModal={state.showSearchFacilityModal}
                refreshTable={refreshData}
            />

            <FacilityContractModal
                colorButton={colorButton}
                secondWideModal={true}
            />
            <FacilityContactModal
                colorButton={colorButton}
                secondWideModal={true}
                hideTable={true}
                facilityID={state.selectedFacility.id}
            />

            <SearchFacilityModal
                showModal={state.showSearchFacilityModal}
                hideModal={hideMergeFacilitiesModal}
                secondWideModal={true}
                selectedFacility={state.selectedFacility}
                colorButton={colorButton}
                mergeFacilities={mergeFacilities}
                countryId={selectedCountry}
            />
        </>
    );
};

const mapStateToProps = (state: IinitialState) => {
    return {
        tableFilters: selectManageFacilityTableFilters(state),
        loading: selectIsLoading(state),
        user: state.user
    };
};

export default withTranslation('manageFacility')(
    connect(mapStateToProps, {
        getFacilitiesByCountry,
        setTableFilter,
        setSelectedFacilityID,
        toggleEditFacilityModal,
        searchFacilityQueueForAsyncSelect,
        beginAjaxCall,
        endAjaxCall
    })(ManangeFacilityQueue)
);
